import React, { useState, useEffect } from 'react';
import { Calculator, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Search } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import { ModalWrapper } from './ModalComponents';

const CreatePositionModal = ({ clientId, clientBalance, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [form, setForm] = useState({ 
    symbol: 'BTCUSDT', 
    side: 'long', 
    entry_price: '', 
    quantity: '', 
    leverage: 10, 
    stop_loss: '', 
    take_profit: '', 
    pnl: '0' 
  });
  const [loading, setLoading] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [symbols, setSymbols] = useState(['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT']);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [symbolSearch, setSymbolSearch] = useState('');
  
  // Calculator state
  const [calcAmount, setCalcAmount] = useState('');
  const [calcPercent, setCalcPercent] = useState('');

  // Load symbols from backend
  useEffect(() => {
    const loadSymbols = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trading/symbols?limit=200`);
        const data = await res.json();
        // Handle both array and object with symbols property
        const symbolsArray = data?.symbols || (Array.isArray(data) ? data : []);
        if (symbolsArray.length > 0) {
          const symbolList = symbolsArray
            .map(s => s.symbol || s)
            .filter(s => typeof s === 'string' && s.endsWith('USDT'))
            .slice(0, 100);
          if (symbolList.length > 0) {
            setSymbols(symbolList);
          }
        }
      } catch (err) {
        console.error('Failed to load symbols:', err);
      } finally {
        setLoadingSymbols(false);
      }
    };
    loadSymbols();
  }, []);

  // Filter symbols by search
  const filteredSymbols = symbols.filter(s => 
    s.toLowerCase().includes(symbolSearch.toLowerCase())
  );

  // Fetch market price on symbol change
  useEffect(() => {
    fetchMarketPrice(form.symbol);
  }, [form.symbol]);

  const fetchMarketPrice = async (symbol) => {
    setLoadingPrice(true);
    try {
      const res = await adminAPI.getMarketPrice(symbol);
      if (res.data?.price) {
        setForm(prev => ({ ...prev, entry_price: res.data.price.toString() }));
      }
    } catch (err) {
      // Fallback prices
      const fallbackPrices = { BTCUSDT: 43500, ETHUSDT: 2250, BNBUSDT: 310, SOLUSDT: 105, XRPUSDT: 0.62, DOGEUSDT: 0.085, ADAUSDT: 0.52, AVAXUSDT: 38, MATICUSDT: 0.85 };
      setForm(prev => ({ ...prev, entry_price: (fallbackPrices[symbol] || 100).toString() }));
    } finally {
      setLoadingPrice(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.entry_price || !form.quantity) return;
    setLoading(true);
    try {
      await adminAPI.createPosition({ 
        user_id: clientId, 
        ...form, 
        entry_price: parseFloat(form.entry_price), 
        quantity: parseFloat(form.quantity), 
        leverage: parseInt(form.leverage), 
        stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null, 
        take_profit: form.take_profit ? parseFloat(form.take_profit) : null, 
        pnl: parseFloat(form.pnl || 0) 
      });
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Calculator functions
  const calculateFromAmount = () => {
    if (!calcAmount || !form.entry_price || !form.leverage) return;
    const price = parseFloat(form.entry_price);
    const amount = parseFloat(calcAmount);
    const leverage = parseInt(form.leverage);
    const quantity = (amount * leverage) / price;
    setForm(prev => ({ ...prev, quantity: quantity.toFixed(6) }));
    setShowCalculator(false);
  };

  const calculateFromPercent = () => {
    if (!calcPercent || !form.entry_price || !form.leverage || !clientBalance) return;
    const price = parseFloat(form.entry_price);
    const percent = parseFloat(calcPercent);
    const leverage = parseInt(form.leverage);
    const amount = (clientBalance * percent) / 100;
    const quantity = (amount * leverage) / price;
    setForm(prev => ({ ...prev, quantity: quantity.toFixed(6) }));
    setShowCalculator(false);
  };

  // Calculate all position data
  const entryPrice = parseFloat(form.entry_price) || 0;
  const quantity = parseFloat(form.quantity) || 0;
  const leverage = parseInt(form.leverage) || 1;
  
  // Position value (notional)
  const positionValue = entryPrice * quantity;
  
  // Margin (collateral required)
  const margin = positionValue / leverage;
  
  // Liquidation price calculation
  // For long: Liquidation when loss = margin (price drops ~100%/leverage)
  // For short: Liquidation when loss = margin (price rises ~100%/leverage)
  const maintenanceMarginRate = 0.005; // 0.5% maintenance margin
  const liquidationPrice = form.side === 'long'
    ? entryPrice * (1 - (1 / leverage) + maintenanceMarginRate)
    : entryPrice * (1 + (1 / leverage) - maintenanceMarginRate);
  
  // Distance to liquidation (%)
  const liquidationDistance = form.side === 'long'
    ? ((entryPrice - liquidationPrice) / entryPrice * 100)
    : ((liquidationPrice - entryPrice) / entryPrice * 100);

  // ROE at different price changes
  const calculateROE = (priceChange) => {
    const newPrice = entryPrice * (1 + priceChange / 100);
    let pnl;
    if (form.side === 'long') {
      pnl = (newPrice - entryPrice) * quantity;
    } else {
      pnl = (entryPrice - newPrice) * quantity;
    }
    return margin > 0 ? (pnl / margin * 100) : 0;
  };

  // Stop Loss / Take Profit PnL preview
  const slPnL = form.stop_loss ? (
    form.side === 'long' 
      ? (parseFloat(form.stop_loss) - entryPrice) * quantity
      : (entryPrice - parseFloat(form.stop_loss)) * quantity
  ) : null;

  const tpPnL = form.take_profit ? (
    form.side === 'long'
      ? (parseFloat(form.take_profit) - entryPrice) * quantity
      : (entryPrice - parseFloat(form.take_profit)) * quantity
  ) : null;

  return (
    <ModalWrapper title="Создать позицию" onClose={onClose} maxWidth="600px">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {/* Symbol with Search */}
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Символ</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
            <input
              type="text"
              value={symbolSearch}
              onChange={(e) => setSymbolSearch(e.target.value)}
              placeholder="Поиск..."
              style={{ width: '100%', padding: '8px 8px 8px 32px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '6px 6px 0 0', color: COLORS.text, fontSize: '12px', boxSizing: 'border-box' }}
            />
          </div>
          <select 
            value={form.symbol} 
            onChange={(e) => setForm({ ...form, symbol: e.target.value })} 
            size={5}
            style={{ width: '100%', padding: '4px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderTop: 'none', borderRadius: '0 0 6px 6px', color: COLORS.text, fontSize: '13px' }}
          >
            {filteredSymbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginTop: '4px' }}>Выбрано: {form.symbol} ({filteredSymbols.length} монет)</div>
        </div>

        {/* Side */}
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Направление</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setForm({ ...form, side: 'long' })}
              style={{ 
                flex: 1, padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: form.side === 'long' ? COLORS.success : COLORS.background,
                color: form.side === 'long' ? '#fff' : COLORS.textSecondary,
                fontWeight: '600'
              }}
            >
              LONG
            </button>
            <button 
              onClick={() => setForm({ ...form, side: 'short' })}
              style={{ 
                flex: 1, padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: form.side === 'short' ? COLORS.danger : COLORS.background,
                color: form.side === 'short' ? '#fff' : COLORS.textSecondary,
                fontWeight: '600'
              }}
            >
              SHORT
            </button>
          </div>
        </div>

        {/* Entry Price */}
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
            Цена входа ($)
            <button 
              onClick={() => fetchMarketPrice(form.symbol)}
              disabled={loadingPrice}
              style={{ marginLeft: '8px', background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer' }}
              title="Обновить рыночную цену"
            >
              <RefreshCw size={12} className={loadingPrice ? 'animate-spin' : ''} />
            </button>
          </label>
          <input 
            type="number" 
            value={form.entry_price} 
            onChange={(e) => setForm({ ...form, entry_price: e.target.value })} 
            placeholder={loadingPrice ? 'Загрузка...' : '0.00'}
            style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
          />
        </div>

        {/* Leverage */}
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Плечо</label>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[5, 10, 20, 50, 100].map(l => (
              <button 
                key={l}
                onClick={() => setForm({ ...form, leverage: l })}
                style={{ 
                  flex: 1, padding: '10px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  backgroundColor: form.leverage === l ? COLORS.primary : COLORS.background,
                  color: form.leverage === l ? '#000' : COLORS.textSecondary,
                  fontSize: '12px', fontWeight: '600'
                }}
              >
                {l}x
              </button>
            ))}
          </div>
        </div>

        {/* Quantity with Calculator */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Объём</label>
            <button 
              onClick={() => setShowCalculator(!showCalculator)}
              style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
            >
              <Calculator size={14} /> Калькулятор
            </button>
          </div>
          <input 
            type="number" 
            value={form.quantity} 
            onChange={(e) => setForm({ ...form, quantity: e.target.value })} 
            placeholder="0.000000"
            step="0.000001"
            style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
          />
        </div>

        {/* Calculator Panel */}
        {showCalculator && (
          <div style={{ gridColumn: '1 / -1', padding: '12px', backgroundColor: `${COLORS.primary}10`, borderRadius: '8px', border: `1px solid ${COLORS.primary}30` }}>
            <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>📐 Калькулятор объёма</div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', marginBottom: '10px' }}>
              <input 
                type="number" 
                value={calcAmount} 
                onChange={(e) => setCalcAmount(e.target.value)} 
                placeholder="Сумма маржи в $"
                style={{ padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '6px', color: COLORS.text, fontSize: '13px' }} 
              />
              <button 
                onClick={calculateFromAmount}
                style={{ padding: '10px 16px', backgroundColor: COLORS.primary, border: 'none', borderRadius: '6px', color: '#000', fontWeight: '600', cursor: 'pointer' }}
              >
                Рассчитать
              </button>
            </div>

            {clientBalance > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[10, 25, 50, 75, 100].map(p => (
                    <button 
                      key={p}
                      onClick={() => setCalcPercent(p.toString())}
                      style={{ 
                        flex: 1, padding: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        backgroundColor: calcPercent === p.toString() ? COLORS.primary : COLORS.background,
                        color: calcPercent === p.toString() ? '#000' : COLORS.textSecondary,
                        fontSize: '11px', fontWeight: '600'
                      }}
                    >
                      {p}%
                    </button>
                  ))}
                </div>
                <button 
                  onClick={calculateFromPercent}
                  style={{ padding: '8px 16px', backgroundColor: COLORS.success, border: 'none', borderRadius: '6px', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '12px' }}
                >
                  % баланса
                </button>
              </div>
            )}
            
            <div style={{ marginTop: '10px', color: COLORS.textSecondary, fontSize: '11px' }}>
              Баланс клиента: ${clientBalance?.toFixed(2) || '0.00'}
            </div>
          </div>
        )}

        {/* Stop Loss & Take Profit */}
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
            Stop Loss ($)
            {slPnL !== null && (
              <span style={{ marginLeft: '8px', color: slPnL >= 0 ? COLORS.success : COLORS.danger, fontSize: '11px' }}>
                {slPnL >= 0 ? '+' : ''}{slPnL.toFixed(2)}$
              </span>
            )}
          </label>
          <input 
            type="number" 
            value={form.stop_loss} 
            onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} 
            placeholder={form.side === 'long' ? `< ${entryPrice.toFixed(2)}` : `> ${entryPrice.toFixed(2)}`}
            style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
          />
        </div>
        
        <div>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
            Take Profit ($)
            {tpPnL !== null && (
              <span style={{ marginLeft: '8px', color: tpPnL >= 0 ? COLORS.success : COLORS.danger, fontSize: '11px' }}>
                {tpPnL >= 0 ? '+' : ''}{tpPnL.toFixed(2)}$
              </span>
            )}
          </label>
          <input 
            type="number" 
            value={form.take_profit} 
            onChange={(e) => setForm({ ...form, take_profit: e.target.value })} 
            placeholder={form.side === 'long' ? `> ${entryPrice.toFixed(2)}` : `< ${entryPrice.toFixed(2)}`}
            style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
          />
        </div>

        {/* PnL Override */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
            Начальный PnL ($) <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>(для отображения)</span>
          </label>
          <input 
            type="number" 
            value={form.pnl} 
            onChange={(e) => setForm({ ...form, pnl: e.target.value })} 
            placeholder="0.00"
            style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
          />
        </div>
      </div>

      {/* Position Summary - Full Details */}
      {form.entry_price && form.quantity && (
        <div style={{ marginTop: '16px', padding: '16px', backgroundColor: COLORS.background, borderRadius: '10px' }}>
          <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {form.side === 'long' ? <TrendingUp size={16} color={COLORS.success} /> : <TrendingDown size={16} color={COLORS.danger} />}
            Параметры позиции
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div style={{ textAlign: 'center', padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Размер позиции</div>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>${positionValue.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Маржа (залог)</div>
              <div style={{ color: COLORS.primary, fontSize: '16px', fontWeight: '700' }}>${margin.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Плечо</div>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>{form.leverage}x</div>
            </div>
          </div>

          {/* Liquidation Info */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px', 
            backgroundColor: `${COLORS.danger}10`, 
            borderRadius: '8px', 
            border: `1px solid ${COLORS.danger}30`,
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={18} color={COLORS.danger} />
              <div>
                <div style={{ color: COLORS.danger, fontSize: '12px', fontWeight: '600' }}>Цена ликвидации</div>
                <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>${liquidationPrice.toFixed(2)}</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>До ликвидации</div>
              <div style={{ color: COLORS.danger, fontSize: '14px', fontWeight: '600' }}>-{liquidationDistance.toFixed(2)}%</div>
            </div>
          </div>

          {/* ROE Preview */}
          <div style={{ padding: '12px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px' }}>ROE при изменении цены:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[1, 5, 10, -1, -5].map(change => {
                const roe = calculateROE(change);
                const isProfit = (form.side === 'long' && change > 0) || (form.side === 'short' && change < 0);
                return (
                  <div 
                    key={change} 
                    style={{ 
                      padding: '6px 10px', 
                      backgroundColor: COLORS.background, 
                      borderRadius: '6px',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ color: COLORS.textSecondary }}>{change > 0 ? '+' : ''}{change}%:</span>
                    <span style={{ color: isProfit ? COLORS.success : COLORS.danger, fontWeight: '600' }}>
                      {roe >= 0 ? '+' : ''}{roe.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <button 
        onClick={handleSubmit} 
        disabled={loading || !form.entry_price || !form.quantity} 
        style={{ 
          width: '100%', 
          marginTop: '16px', 
          padding: '14px', 
          backgroundColor: loading || !form.entry_price ? COLORS.border : form.side === 'long' ? COLORS.success : COLORS.danger, 
          border: 'none', 
          borderRadius: '8px', 
          color: '#fff', 
          fontSize: '15px', 
          fontWeight: '600', 
          cursor: loading || !form.entry_price ? 'not-allowed' : 'pointer' 
        }}
      >
        {loading ? 'Создание...' : `Открыть ${form.side.toUpperCase()} ${form.symbol} • Маржа $${margin.toFixed(2)}`}
      </button>
    </ModalWrapper>
  );
};

export default CreatePositionModal;
